import { useState } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'
import type { MarketingReport, KeyArtConcept, AudienceSimulation, GeoRegion } from '../types'
import { GEO_REGIONS } from '../types'
import { generateKeyArt, generateAudienceSimulation, type ChatSession } from '../services/aiService'
import { historyService } from '../services/historyService'
import { ChatInterface } from './ChatInterface'

interface MarketingReportDisplayProps {
  report: MarketingReport
  chatSession: ChatSession | null
  onNewReport: () => void
}

const CHART_COLORS = ['#ff2a6d', '#05d9e8', '#9d4edd', '#00ff88', '#ffd700', '#ff6b35']

export function MarketingReportDisplay({ report, chatSession, onNewReport }: MarketingReportDisplayProps) {
  const [keyArtConcepts, setKeyArtConcepts] = useState<KeyArtConcept[]>(report.keyArtConcepts)
  const [activeSection, setActiveSection] = useState<string>('info')
  const [activeRegion, setActiveRegion] = useState<GeoRegion>(report.selectedRegions[0] || 'USCAN')
  const [simulation, setSimulation] = useState<AudienceSimulation | null>(report.simulation || null)
  const [isSimulating, setIsSimulating] = useState(false)
  const [simulationError, setSimulationError] = useState<string | null>(null)

  // Get the active region's marketing plan
  const activeMarketingPlan = report.regionalPlans[activeRegion]

  // Get region label for display
  const getRegionLabel = (region: GeoRegion) => {
    const info = GEO_REGIONS.find(r => r.value === region)
    return info ? info.label : region
  }

  const handleGenerateArt = async (conceptId: string) => {
    const concept = keyArtConcepts.find(c => c.id === conceptId)
    if (!concept || concept.isGenerating) return

    setKeyArtConcepts(prev =>
      prev.map(c => c.id === conceptId ? { ...c, isGenerating: true } : c)
    )

    try {
      const imageUrl = await generateKeyArt(concept.generativePrompt)
      const updatedConcepts = keyArtConcepts.map(c => c.id === conceptId ? { ...c, generatedImageUrl: imageUrl, isGenerating: false } : c)
      setKeyArtConcepts(updatedConcepts)
      
      // Save permanently to history
      report.keyArtConcepts = updatedConcepts
      historyService.saveReport(report)
    } catch (error) {
      console.error('Failed to generate art:', error)
      setKeyArtConcepts(prev =>
        prev.map(c => c.id === conceptId ? { ...c, isGenerating: false } : c)
      )
    }
  }

  const [isExportingPdf, setIsExportingPdf] = useState(false)

  const handleExportPdf = async () => {
    setIsExportingPdf(true)

    // Wait for React 18 to flush the DOM update
    setTimeout(async () => {
      const element = document.getElementById('pdf-export-content')
      if (!element) {
        setIsExportingPdf(false)
        console.error("PDF container not found")
        return
      }

      try {
        const html2pdfModule = await import('html2pdf.js')
        const html2pdf = html2pdfModule.default || html2pdfModule
        
        await html2pdf()
          .set({
            margin: [15, 10, 15, 10],
            filename: `${report.showInfo.title.replace(/\s+/g, '_')}_marketing_strategy.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
          })
          .from(element)
          .save()
      } catch (error) {
        console.error("PDF Export Error:", error)
      } finally {
        setIsExportingPdf(false)
      }
    }, 150)
  }

  const handleCopyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(report, null, 2))
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const handleRunSimulation = async () => {
    if (isSimulating) return
    setIsSimulating(true)
    setSimulationError(null)

    try {
      const result = await generateAudienceSimulation(report)
      setSimulation(result)
      report.simulation = result
      historyService.saveReport(report)
    } catch (error) {
      console.error('Simulation failed:', error)
      setSimulationError(error instanceof Error ? error.message : 'Simulation failed')
    } finally {
      setIsSimulating(false)
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-400'
    if (score >= 6) return 'text-yellow-400'
    if (score >= 4) return 'text-orange-400'
    return 'text-red-400'
  }

  const getScoreBgColor = (score: number) => {
    if (score >= 8) return 'bg-green-500/20 border-green-500/30'
    if (score >= 6) return 'bg-yellow-500/20 border-yellow-500/30'
    if (score >= 4) return 'bg-orange-500/20 border-orange-500/30'
    return 'bg-red-500/20 border-red-500/30'
  }

  const baseSections = [
    { id: 'info', label: 'Info', icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { id: 'audience', label: 'Audience', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
    { id: 'competitors', label: 'Competitors', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    { id: 'marketing', label: 'Marketing', icon: 'M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z' },
  ]

  const fycSection = { id: 'fyc', label: 'FYC Awards', icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z' }

  const simulationSection = { id: 'simulation', label: 'Simulation', icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' }

  const finalSections = [
    ...baseSections,
    ...(report.campaignType === 'fyc' ? [fycSection] : []),
    { id: 'keyart', label: 'Key Art', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
    simulationSection,
    { id: 'chat', label: 'Chat', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
  ]

  const sections = finalSections

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-2 py-1 text-xs font-display uppercase rounded ${
              report.mediaType === 'tv' ? 'bg-cyber-pink/20 text-cyber-pink' : 'bg-cyber-blue/20 text-cyber-blue'
            }`}>
              {report.mediaType === 'tv' ? 'TV Show' : 'Movie'}
            </span>
            <span className={`px-2 py-1 text-xs font-display uppercase rounded ${
              report.campaignType === 'fyc' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'
            }`}>
              {report.campaignType === 'fyc' ? 'FYC Awards' : 'Consumer'}
            </span>
            <span className="px-2 py-1 text-xs font-mono rounded bg-cyber-purple/20 text-cyber-purple">
              {report.modelUsed}
            </span>
            <span className="px-2 py-1 text-xs font-mono rounded bg-cyan-500/20 text-cyan-400">
              {report.selectedRegions.map(getRegionLabel).join(', ')}
            </span>
          </div>
          <h1 className="cyber-heading text-2xl sm:text-3xl mt-2">
            {report.showInfo.title}
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Generated {new Date(report.createdAt).toLocaleDateString()}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleCopyJson} className="cyber-button-secondary" title="Copy JSON">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          <button onClick={handleExportPdf} className="cyber-button-secondary" title="Export PDF">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
          <button onClick={onNewReport} className="cyber-button-secondary">
            New Report
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
        {sections.map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-display text-sm uppercase tracking-wider whitespace-nowrap transition-all ${
              activeSection === section.id
                ? 'bg-cyber-blue text-cyber-dark shadow-neon-blue'
                : 'bg-cyber-gray text-gray-400 hover:text-white'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={section.icon} />
            </svg>
            {section.label}
          </button>
        ))}
      </div>

      {/* Report Content */}
      <div id="report-content">
        {/* Show Info Section */}
        {activeSection === 'info' && (
          <div className="cyber-card space-y-6">
            <h2 className="cyber-heading text-xl">Show Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="space-y-4">
                  <div>
                    <span className="text-cyber-blue text-xs uppercase tracking-wider">Genre</span>
                    <p className="text-white">{report.showInfo.genre}{report.showInfo.subGenre && ` / ${report.showInfo.subGenre}`}</p>
                  </div>
                  {report.showInfo.network && (
                    <div>
                      <span className="text-cyber-blue text-xs uppercase tracking-wider">Network</span>
                      <p className="text-white">{report.showInfo.network}</p>
                    </div>
                  )}
                  {report.showInfo.studio && (
                    <div>
                      <span className="text-cyber-blue text-xs uppercase tracking-wider">Studio</span>
                      <p className="text-white">{report.showInfo.studio}</p>
                    </div>
                  )}
                  {report.showInfo.releaseDate && (
                    <div>
                      <span className="text-cyber-blue text-xs uppercase tracking-wider">Release Date</span>
                      <p className="text-white">{report.showInfo.releaseDate}</p>
                    </div>
                  )}
                  {report.showInfo.rating && (
                    <div>
                      <span className="text-cyber-blue text-xs uppercase tracking-wider">Rating</span>
                      <p className="text-white">{report.showInfo.rating}</p>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div className="space-y-4">
                  <div>
                    <span className="text-cyber-blue text-xs uppercase tracking-wider">Cast</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {report.showInfo.cast.map((actor, i) => (
                        <span key={i} className="px-2 py-1 bg-cyber-gray rounded text-sm">{actor}</span>
                      ))}
                    </div>
                  </div>
                  {report.showInfo.creators && (
                    <div>
                      <span className="text-cyber-blue text-xs uppercase tracking-wider">Creators</span>
                      <p className="text-white">{report.showInfo.creators.join(', ')}</p>
                    </div>
                  )}
                  {report.showInfo.director && (
                    <div>
                      <span className="text-cyber-blue text-xs uppercase tracking-wider">Director</span>
                      <p className="text-white">{report.showInfo.director}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div>
              <span className="text-cyber-blue text-xs uppercase tracking-wider">Summary</span>
              <p className="text-gray-300 mt-1">{report.showInfo.summary}</p>
            </div>
            {report.groundingUrls && report.groundingUrls.length > 0 && (
              <div>
                <span className="text-cyber-blue text-xs uppercase tracking-wider">Sources</span>
                <div className="mt-2 space-y-1">
                  {report.groundingUrls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block text-sm text-cyber-purple hover:text-cyber-pink truncate">
                      {url}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Audience Section */}
        {activeSection === 'audience' && (
          <div className="cyber-card space-y-6">
            <h2 className="cyber-heading text-xl">Audience Profile</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-cyber-darker p-4 rounded-lg">
                <span className="text-cyber-pink text-xs uppercase tracking-wider">Primary Age</span>
                <p className="text-2xl font-display mt-1">
                  {report.audienceProfile.primaryAge.min}-{report.audienceProfile.primaryAge.max}
                </p>
                {report.audienceProfile.secondaryAge && (
                  <p className="text-sm text-gray-400 mt-1">
                    Secondary: {report.audienceProfile.secondaryAge.min}-{report.audienceProfile.secondaryAge.max}
                  </p>
                )}
              </div>
              <div className="bg-cyber-darker p-4 rounded-lg">
                <span className="text-cyber-pink text-xs uppercase tracking-wider">Gender Split</span>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Male</span>
                    <span>{report.audienceProfile.gender.male}%</span>
                  </div>
                  <div className="w-full bg-cyber-gray rounded-full h-2">
                    <div className="bg-cyber-blue h-2 rounded-full" style={{ width: `${report.audienceProfile.gender.male}%` }} />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Female</span>
                    <span>{report.audienceProfile.gender.female}%</span>
                  </div>
                  <div className="w-full bg-cyber-gray rounded-full h-2">
                    <div className="bg-cyber-pink h-2 rounded-full" style={{ width: `${report.audienceProfile.gender.female}%` }} />
                  </div>
                </div>
              </div>
              <div className="bg-cyber-darker p-4 rounded-lg">
                <span className="text-cyber-pink text-xs uppercase tracking-wider">Income Distribution</span>
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Low</span>
                    <span>{report.audienceProfile.income.low}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Middle</span>
                    <span>{report.audienceProfile.income.middle}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>High</span>
                    <span>{report.audienceProfile.income.high}%</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <span className="text-cyber-blue text-xs uppercase tracking-wider">Top Locations</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {report.audienceProfile.locations.map((loc, i) => (
                    <span key={i} className="px-3 py-1 bg-cyber-gray rounded-full text-sm">{loc}</span>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-cyber-blue text-xs uppercase tracking-wider">Interests</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {report.audienceProfile.interests.map((int, i) => (
                    <span key={i} className="px-3 py-1 bg-cyber-purple/20 text-cyber-purple rounded-full text-sm">{int}</span>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <span className="text-cyber-blue text-xs uppercase tracking-wider">Psychographics</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {report.audienceProfile.psychographics.map((p, i) => (
                  <span key={i} className="px-3 py-1 bg-cyber-pink/20 text-cyber-pink rounded-full text-sm">{p}</span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-cyber-blue text-xs uppercase tracking-wider">Media Consumption</span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-2">
                <div className="text-center">
                  <div className="text-2xl font-display text-cyber-blue">{report.audienceProfile.mediaConsumption.streaming}%</div>
                  <div className="text-xs text-gray-400">Streaming</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-display text-cyber-pink">{report.audienceProfile.mediaConsumption.linear}%</div>
                  <div className="text-xs text-gray-400">Linear TV</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-display text-cyber-purple">{report.audienceProfile.mediaConsumption.social}%</div>
                  <div className="text-xs text-gray-400">Social</div>
                </div>
                {report.audienceProfile.mediaConsumption.theatrical !== undefined && (
                  <div className="text-center">
                    <div className="text-2xl font-display text-green-400">{report.audienceProfile.mediaConsumption.theatrical}%</div>
                    <div className="text-xs text-gray-400">Theatrical</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Competitors Section */}
        {activeSection === 'competitors' && (
          <div className="cyber-card space-y-6">
            <h2 className="cyber-heading text-xl">Competitor Analysis</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {report.competitorAnalysis.competitors.map((comp, i) => (
                <div key={i} className="bg-cyber-darker p-4 rounded-lg border border-cyber-blue/20">
                  <h3 className="font-display text-lg text-white mb-2">{comp.title}</h3>
                  <p className="text-sm text-gray-400 mb-3">{comp.similarity}</p>
                  <div className="mb-3">
                    <span className="text-cyber-pink text-xs uppercase tracking-wider">Performance</span>
                    <p className="text-sm">{comp.performance.metric}: {comp.performance.value}</p>
                  </div>
                  <div className="mb-3">
                    <span className="text-cyber-blue text-xs uppercase tracking-wider">Success Factors</span>
                    <ul className="text-sm text-gray-300 list-disc list-inside mt-1">
                      {comp.successFactors.map((f, j) => <li key={j}>{f}</li>)}
                    </ul>
                  </div>
                  <div>
                    <span className="text-cyber-purple text-xs uppercase tracking-wider">Lessons</span>
                    <ul className="text-sm text-gray-300 list-disc list-inside mt-1">
                      {comp.lessons.map((l, j) => <li key={j}>{l}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <span className="text-cyber-blue text-xs uppercase tracking-wider">Market Gap</span>
                <p className="text-gray-300 mt-1">{report.competitorAnalysis.marketGap}</p>
              </div>
              <div>
                <span className="text-cyber-blue text-xs uppercase tracking-wider">Positioning</span>
                <p className="text-gray-300 mt-1">{report.competitorAnalysis.positioning}</p>
              </div>
            </div>
            <div>
              <span className="text-cyber-blue text-xs uppercase tracking-wider">Differentiators</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {report.competitorAnalysis.differentiators.map((d, i) => (
                  <span key={i} className="px-3 py-1 bg-cyber-pink/20 text-cyber-pink rounded-full text-sm">{d}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Marketing Plan Section */}
        {activeSection === 'marketing' && (
          <div className="space-y-6">
            {/* Region Tabs */}
            {report.selectedRegions.length > 1 && (
              <div className="cyber-card">
                <h2 className="cyber-heading text-xl mb-4">Select Region</h2>
                <div className="flex flex-wrap gap-2">
                  {report.selectedRegions.map((region) => (
                    <button
                      key={region}
                      onClick={() => setActiveRegion(region)}
                      className={`px-4 py-2 rounded-lg font-display text-sm uppercase tracking-wider transition-all ${
                        activeRegion === region
                          ? 'bg-cyan-500 text-cyber-dark shadow-neon-blue'
                          : 'bg-cyber-gray text-gray-400 hover:text-white'
                      }`}
                    >
                      {getRegionLabel(region)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeMarketingPlan && activeMarketingPlan.budgetBreakdown ? (
              <>
                {/* Budget Overview */}
                <div className="cyber-card">
                  <h2 className="cyber-heading text-xl mb-6">
                    {report.selectedRegions.length > 1 ? `${getRegionLabel(activeRegion)} ` : ''}Budget Overview
                  </h2>
                  <div className="text-center mb-4">
                    <span className="text-cyber-blue text-xs uppercase tracking-wider">Total Budget</span>
                    <p className="text-3xl font-display text-white">{formatCurrency(activeMarketingPlan.totalBudget || 0)}</p>
                  </div>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={activeMarketingPlan.budgetBreakdown || []}
                          dataKey="amount"
                          nameKey="category"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ category, percentage }) => `${category} (${percentage}%)`}
                        >
                          {(activeMarketingPlan.budgetBreakdown || []).map((_, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number) => formatCurrency(value)}
                          contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #05d9e8' }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Ad Placements */}
                <div className="cyber-card">
                  <h3 className="cyber-heading text-lg mb-4">Ad Placements</h3>
                  <div className="space-y-4">
                    {(activeMarketingPlan.adPlacements || []).map((ad, i) => (
                      <div key={i} className="bg-cyber-darker p-4 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-display text-white">{ad.platform}</h4>
                            <p className="text-sm text-gray-400">{ad.format}</p>
                          </div>
                          <span className="text-cyber-blue font-mono">{formatCurrency(ad.budget)}</span>
                        </div>
                        <p className="text-sm text-gray-300 mt-2">{ad.reasoning}</p>
                        <div className="flex gap-4 mt-2 text-xs text-gray-500">
                          <span>Reach: {ad.expectedReach}</span>
                          <span>Timing: {ad.timing}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Social Strategy */}
                <div className="cyber-card">
                  <h3 className="cyber-heading text-lg mb-4">Social Strategy</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(activeMarketingPlan.socialStrategy || []).map((social, i) => (
                      <div key={i} className="bg-cyber-darker p-4 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-display text-white">{social.platform}</h4>
                          <span className="text-cyber-pink font-mono text-sm">{formatCurrency(social.budget)}</span>
                        </div>
                        <p className="text-sm text-gray-400 mb-2">Frequency: {social.frequency}</p>
                        <div className="mb-2">
                          <span className="text-xs text-cyber-blue">Content Types:</span>
                          <ul className="text-sm text-gray-300 list-disc list-inside">
                            {social.content.map((c, j) => <li key={j}>{c}</li>)}
                          </ul>
                        </div>
                        {social.influencerStrategy && (
                          <p className="text-sm text-gray-400">Influencers: {social.influencerStrategy}</p>
                        )}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {social.hashtags.map((h, j) => (
                            <span key={j} className="text-xs text-cyber-purple">#{h}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cross Promotions & Events */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="cyber-card">
                    <h3 className="cyber-heading text-lg mb-4">Cross Promotions</h3>
                    <div className="space-y-3">
                      {(activeMarketingPlan.crossPromotions || []).map((promo, i) => (
                        <div key={i} className="bg-cyber-darker p-3 rounded-lg">
                          <div className="flex justify-between">
                            <span className="font-medium">{promo.partner}</span>
                            <span className="text-cyber-blue text-sm">{formatCurrency(promo.budget)}</span>
                          </div>
                          <p className="text-xs text-gray-400">{promo.type}</p>
                          <p className="text-sm text-gray-300 mt-1">{promo.description}</p>
                          <p className="text-xs text-cyber-purple mt-1">Impact: {promo.expectedImpact}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="cyber-card">
                    <h3 className="cyber-heading text-lg mb-4">Stunts & Events</h3>
                    <div className="space-y-3">
                      {(activeMarketingPlan.stuntsEvents || []).map((event, i) => (
                        <div key={i} className="bg-cyber-darker p-3 rounded-lg">
                          <div className="flex justify-between">
                            <span className="font-medium">{event.name}</span>
                            <span className="text-cyber-pink text-sm">{formatCurrency(event.budget)}</span>
                          </div>
                          <p className="text-xs text-gray-400">{event.type} | {event.location} | {event.timing}</p>
                          <p className="text-sm text-gray-300 mt-1">{event.description}</p>
                          <p className="text-xs text-green-400 mt-1">Media Value: {event.mediaValue}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="cyber-card">
                  <h3 className="cyber-heading text-lg mb-4">Campaign Timeline</h3>
                  <div className="space-y-4">
                    {(activeMarketingPlan.timeline || []).map((phase, i) => (
                      <div key={i} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className="w-4 h-4 rounded-full bg-cyber-blue" />
                          {i < (activeMarketingPlan.timeline || []).length - 1 && (
                            <div className="w-0.5 flex-1 bg-cyber-blue/30" />
                          )}
                        </div>
                        <div className="flex-1 pb-4">
                          <div className="flex justify-between items-center">
                            <h4 className="font-display text-white">{phase.phase}</h4>
                            <span className="text-sm text-gray-400">{phase.duration}</span>
                          </div>
                          <ul className="text-sm text-gray-300 list-disc list-inside mt-2">
                            {phase.activities.map((a, j) => <li key={j}>{a}</li>)}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="cyber-card text-center py-12">
                <p className="text-gray-400 mb-2">No marketing plan generated for {getRegionLabel(activeRegion)}</p>
                <p className="text-sm text-gray-500">
                  This can happen when selecting multiple regions due to response size limits.
                  <br />Try regenerating with fewer regions or using the Pro (Opus 4.6) model.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Key Art Section */}
        {activeSection === 'keyart' && (
          <div className="cyber-card space-y-6">
            <h2 className="cyber-heading text-xl">Key Art Concepts</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {keyArtConcepts.map((concept) => (
                <div key={concept.id} className="bg-cyber-darker rounded-lg overflow-hidden">
                  {/* Image Area */}
                  <div className="aspect-video bg-cyber-gray relative">
                    {concept.generatedImageUrl ? (
                      <img
                        src={concept.generatedImageUrl}
                        alt={concept.name}
                        className="w-full h-full object-cover"
                      />
                    ) : concept.isGenerating ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-10 h-10 border-4 border-cyber-blue border-t-transparent rounded-full animate-spin mx-auto" />
                          <p className="text-sm text-gray-400 mt-2">Generating...</p>
                        </div>
                      </div>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <button
                          onClick={() => handleGenerateArt(concept.id)}
                          className="cyber-button text-sm"
                        >
                          Generate Art
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-display text-lg text-white">{concept.name}</h3>
                    <p className="text-sm text-gray-400 mt-1">{concept.description}</p>
                    <div className="mt-3">
                      <span className="text-cyber-blue text-xs uppercase tracking-wider">Mood</span>
                      <p className="text-sm">{concept.mood}</p>
                    </div>
                    <div className="mt-2">
                      <span className="text-cyber-pink text-xs uppercase tracking-wider">Tagline</span>
                      <p className="text-sm italic">"{concept.tagline}"</p>
                    </div>
                    <div className="mt-2">
                      <span className="text-cyber-purple text-xs uppercase tracking-wider">Color Palette</span>
                      <div className="flex gap-2 mt-1">
                        {concept.colorPalette.map((color, i) => (
                          <span key={i} className="px-2 py-0.5 bg-cyber-gray rounded text-xs">{color}</span>
                        ))}
                      </div>
                    </div>
                    <details className="mt-3">
                      <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-300">
                        View Generative Prompt
                      </summary>
                      <p className="text-xs text-gray-400 mt-2 p-2 bg-cyber-gray rounded">
                        {concept.generativePrompt}
                      </p>
                    </details>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FYC Awards Section */}
        {activeSection === 'fyc' && report.fycStrategy && (
          <div className="space-y-6">
            {/* Overall Narrative */}
            <div className="cyber-card">
              <h2 className="cyber-heading text-xl mb-4">FYC Campaign Narrative</h2>
              <p className="text-gray-300">{report.fycStrategy.overallNarrative}</p>
            </div>

            {/* Awards Categories */}
            <div className="cyber-card">
              <h2 className="cyber-heading text-xl mb-4">Recommended Categories</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {report.fycStrategy.primaryCategories.map((cat, i) => (
                  <div key={i} className="bg-cyber-darker p-4 rounded-lg border border-yellow-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-display text-lg text-yellow-400">{cat.category}</h3>
                      <span className={`px-2 py-1 text-xs rounded uppercase ${
                        cat.likelihood === 'high' ? 'bg-green-500/20 text-green-400' :
                        cat.likelihood === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {cat.likelihood} likelihood
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 mb-3">{cat.reasoning}</p>
                    <div className="space-y-2">
                      <div>
                        <span className="text-cyber-blue text-xs uppercase tracking-wider">Campaign Angle</span>
                        <p className="text-sm text-gray-300">{cat.campaignAngle}</p>
                      </div>
                      <div>
                        <span className="text-cyber-pink text-xs uppercase tracking-wider">Voter Messaging</span>
                        <p className="text-sm text-gray-300">{cat.voterMessaging}</p>
                      </div>
                      <div>
                        <span className="text-cyber-purple text-xs uppercase tracking-wider">Competition</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {cat.competingTitles.map((title, j) => (
                            <span key={j} className="px-2 py-0.5 bg-cyber-gray rounded text-xs">{title}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Screenings */}
            <div className="cyber-card">
              <h2 className="cyber-heading text-xl mb-4">FYC Screenings</h2>
              <div className="space-y-4">
                {report.fycStrategy.screenings.map((screening, i) => (
                  <div key={i} className="bg-cyber-darker p-4 rounded-lg">
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                      <div>
                        <h3 className="font-display text-lg text-white">{screening.name}</h3>
                        <p className="text-sm text-gray-400">{screening.venue} | {screening.location}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-cyber-blue font-mono">{formatCurrency(screening.budget)}</span>
                        <p className="text-xs text-gray-500">{screening.date}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-300 mb-3">{screening.description}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-cyber-pink text-xs uppercase tracking-wider">Target Attendees</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {screening.targetAttendees.map((a, j) => (
                            <span key={j} className="px-2 py-0.5 bg-cyber-pink/20 text-cyber-pink rounded text-xs">{a}</span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className="text-cyber-purple text-xs uppercase tracking-wider">Talent Attending</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {screening.talentAttending.map((t, j) => (
                            <span key={j} className="px-2 py-0.5 bg-cyber-purple/20 text-cyber-purple rounded text-xs">{t}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    {screening.qAndA && (
                      <div className="mt-2 text-xs text-green-400">Includes Q&A Session</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Voter Outreach */}
            <div className="cyber-card">
              <h2 className="cyber-heading text-xl mb-4">Branch Outreach Strategy</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {report.fycStrategy.voterOutreach.map((outreach, i) => (
                  <div key={i} className="bg-cyber-darker p-4 rounded-lg">
                    <h3 className="font-display text-lg text-cyber-blue mb-2">{outreach.branch}</h3>
                    <p className="text-sm text-gray-400 mb-3">{outreach.strategy}</p>
                    <div className="space-y-2">
                      <div>
                        <span className="text-cyber-pink text-xs uppercase tracking-wider">Key Messages</span>
                        <ul className="text-sm text-gray-300 list-disc list-inside mt-1">
                          {outreach.keyMessages.map((msg, j) => <li key={j}>{msg}</li>)}
                        </ul>
                      </div>
                      <div>
                        <span className="text-cyber-purple text-xs uppercase tracking-wider">Touchpoints</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {outreach.touchpoints.map((tp, j) => (
                            <span key={j} className="px-2 py-0.5 bg-cyber-gray rounded text-xs">{tp}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Trade Ads */}
            <div className="cyber-card">
              <h2 className="cyber-heading text-xl mb-4">Trade Ad Placements</h2>
              <div className="space-y-3">
                {report.fycStrategy.tradeAdPlacements.map((ad, i) => (
                  <div key={i} className="bg-cyber-darker p-4 rounded-lg flex flex-wrap justify-between items-start gap-2">
                    <div>
                      <h3 className="font-display text-white">{ad.publication}</h3>
                      <p className="text-sm text-gray-400">{ad.format} | {ad.timing}</p>
                      <p className="text-sm text-gray-300 mt-1">{ad.messaging}</p>
                    </div>
                    <span className="text-cyber-blue font-mono">{formatCurrency(ad.budget)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Guild Screenings */}
            <div className="cyber-card">
              <h2 className="cyber-heading text-xl mb-4">Guild Screenings</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {report.fycStrategy.guildScreenings.map((guild, i) => (
                  <div key={i} className="bg-cyber-darker p-4 rounded-lg">
                    <h3 className="font-display text-yellow-400">{guild.guild}</h3>
                    <p className="text-sm text-gray-400">{guild.date} | {guild.location}</p>
                    <p className="text-sm text-gray-300 mt-2">{guild.strategy}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* FYC Budget */}
            <div className="cyber-card">
              <h2 className="cyber-heading text-xl mb-6">FYC Campaign Budget</h2>
              <div className="text-center mb-4">
                <span className="text-yellow-400 text-xs uppercase tracking-wider">Total FYC Budget</span>
                <p className="text-3xl font-display text-white">{formatCurrency(report.fycStrategy.totalBudget)}</p>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={report.fycStrategy.budgetBreakdown}
                      dataKey="amount"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ category, percentage }) => `${category} (${percentage}%)`}
                    >
                      {report.fycStrategy.budgetBreakdown.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #ffd700' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* FYC Timeline */}
            <div className="cyber-card">
              <h2 className="cyber-heading text-xl mb-4">Awards Season Timeline</h2>
              <div className="space-y-4">
                {report.fycStrategy.timeline.map((phase, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 rounded-full bg-yellow-400" />
                      {i < report.fycStrategy!.timeline.length - 1 && (
                        <div className="w-0.5 flex-1 bg-yellow-400/30" />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex justify-between items-center">
                        <h4 className="font-display text-white">{phase.phase}</h4>
                        <span className="text-sm text-gray-400">{phase.duration}</span>
                      </div>
                      <ul className="text-sm text-gray-300 list-disc list-inside mt-2">
                        {phase.activities.map((a, j) => <li key={j}>{a}</li>)}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Simulation Section */}
        {activeSection === 'simulation' && (
          <div className="space-y-6">
            {!simulation && !isSimulating && (
              <div className="cyber-card text-center py-12">
                <div className="max-w-md mx-auto">
                  <svg className="w-16 h-16 mx-auto text-cyber-purple mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <h2 className="cyber-heading text-xl mb-2">Audience Simulation</h2>
                  <p className="text-gray-400 mb-6">
                    Generate AI personas based on your target audience and simulate their reactions to your campaign elements.
                  </p>
                  {simulationError && (
                    <p className="text-red-400 text-sm mb-4">{simulationError}</p>
                  )}
                  <button onClick={handleRunSimulation} className="cyber-button">
                    Run Simulation
                  </button>
                </div>
              </div>
            )}

            {isSimulating && (
              <div className="cyber-card text-center py-12">
                <div className="w-16 h-16 border-4 border-cyber-purple border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-400">Generating personas and simulating reactions...</p>
                <p className="text-xs text-gray-500 mt-2">This may take a moment</p>
              </div>
            )}

            {simulation && (
              <>
                {/* Overall Score */}
                <div className="cyber-card">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h2 className="cyber-heading text-xl mb-2">Campaign Resonance Score</h2>
                      <p className="text-gray-400">{simulation.summary}</p>
                    </div>
                    <div className={`text-5xl font-display ${getScoreColor(simulation.overallScore)}`}>
                      {simulation.overallScore.toFixed(1)}
                      <span className="text-lg text-gray-500">/10</span>
                    </div>
                  </div>
                </div>

                {/* Top Recommendations */}
                <div className="cyber-card">
                  <h2 className="cyber-heading text-xl mb-4">Top Recommendations</h2>
                  <div className="space-y-2">
                    {simulation.topRecommendations.map((rec, i) => (
                      <div key={i} className="flex items-start gap-3 bg-cyber-darker p-3 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-cyber-blue/20 text-cyber-blue flex items-center justify-center text-sm font-bold shrink-0">
                          {i + 1}
                        </div>
                        <p className="text-gray-300">{rec}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Personas */}
                <div className="cyber-card">
                  <h2 className="cyber-heading text-xl mb-4">Audience Personas</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {simulation.personas.map((persona) => {
                      const reaction = simulation.reactions.find(r => r.personaId === persona.id)
                      return (
                        <div key={persona.id} className={`bg-cyber-darker p-4 rounded-lg border ${getScoreBgColor(reaction?.overallResonance || 0)}`}>
                          <div className="text-center mb-3">
                            <span className="text-4xl">{persona.avatar}</span>
                            <h3 className="font-display text-white mt-2">{persona.name}</h3>
                            <p className="text-xs text-gray-500">{persona.age}, {persona.gender}</p>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-cyber-blue text-xs uppercase">Occupation</span>
                              <p className="text-gray-300">{persona.occupation}</p>
                            </div>
                            <div>
                              <span className="text-cyber-blue text-xs uppercase">Location</span>
                              <p className="text-gray-300">{persona.location}</p>
                            </div>
                            <div>
                              <span className="text-cyber-pink text-xs uppercase">Interests</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {persona.interests.slice(0, 3).map((int, j) => (
                                  <span key={j} className="px-1.5 py-0.5 bg-cyber-gray rounded text-xs">{int}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                          {reaction && (
                            <div className="mt-3 pt-3 border-t border-gray-700">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500">Resonance</span>
                                <span className={`font-display text-lg ${getScoreColor(reaction.overallResonance)}`}>
                                  {reaction.overallResonance.toFixed(1)}
                                </span>
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                Likely to watch: <span className={
                                  reaction.likelyToWatch === 'definitely' ? 'text-green-400' :
                                  reaction.likelyToWatch === 'probably' ? 'text-yellow-400' :
                                  reaction.likelyToWatch === 'maybe' ? 'text-orange-400' :
                                  'text-red-400'
                                }>{reaction.likelyToWatch}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Ranked Elements */}
                <div className="cyber-card">
                  <h2 className="cyber-heading text-xl mb-4">Campaign Element Rankings</h2>
                  <div className="space-y-3">
                    {simulation.rankedElements.map((element, i) => (
                      <div key={i} className="bg-cyber-darker p-4 rounded-lg">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-display ${
                              i === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                              i === 1 ? 'bg-gray-400/20 text-gray-300' :
                              i === 2 ? 'bg-orange-600/20 text-orange-400' :
                              'bg-cyber-gray text-gray-500'
                            }`}>
                              {i + 1}
                            </div>
                            <div>
                              <h3 className="font-display text-white">{element.element}</h3>
                              <span className={`text-xs uppercase px-2 py-0.5 rounded ${
                                element.elementType === 'ad' ? 'bg-cyber-blue/20 text-cyber-blue' :
                                element.elementType === 'social' ? 'bg-cyber-pink/20 text-cyber-pink' :
                                element.elementType === 'event' ? 'bg-green-500/20 text-green-400' :
                                element.elementType === 'promo' ? 'bg-cyber-purple/20 text-cyber-purple' :
                                'bg-yellow-500/20 text-yellow-400'
                              }`}>{element.elementType}</span>
                            </div>
                          </div>
                          <div className={`text-2xl font-display ${getScoreColor(element.averageScore)}`}>
                            {element.averageScore.toFixed(1)}
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="text-green-400 text-xs uppercase">Strengths</span>
                            <ul className="text-sm text-gray-300 list-disc list-inside mt-1">
                              {element.strengths.map((s, j) => <li key={j}>{s}</li>)}
                            </ul>
                          </div>
                          <div>
                            <span className="text-red-400 text-xs uppercase">Weaknesses</span>
                            <ul className="text-sm text-gray-300 list-disc list-inside mt-1">
                              {element.weaknesses.map((w, j) => <li key={j}>{w}</li>)}
                            </ul>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {element.personaScores.map((ps) => {
                            const persona = simulation.personas.find(p => p.id === ps.personaId)
                            return (
                              <div key={ps.personaId} className="flex items-center gap-1 bg-cyber-gray px-2 py-1 rounded text-xs">
                                <span>{persona?.avatar}</span>
                                <span className={getScoreColor(ps.score)}>{ps.score}</span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Detailed Persona Reactions */}
                <div className="cyber-card">
                  <h2 className="cyber-heading text-xl mb-4">Detailed Persona Feedback</h2>
                  <div className="space-y-6">
                    {simulation.reactions.map((reaction) => {
                      const persona = simulation.personas.find(p => p.id === reaction.personaId)
                      if (!persona) return null
                      return (
                        <div key={reaction.personaId} className="bg-cyber-darker p-4 rounded-lg">
                          <div className="flex flex-wrap items-center gap-3 mb-4">
                            <span className="text-3xl">{persona.avatar}</span>
                            <div>
                              <h3 className="font-display text-white">{persona.name}</h3>
                              <p className="text-sm text-gray-400">{persona.occupation} | {persona.location}</p>
                            </div>
                            <div className={`ml-auto text-3xl font-display ${getScoreColor(reaction.overallResonance)}`}>
                              {reaction.overallResonance.toFixed(1)}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                              <span className="text-green-400 text-xs uppercase">Top Strengths</span>
                              <ul className="text-sm text-gray-300 list-disc list-inside mt-1">
                                {reaction.topStrengths.map((s, j) => <li key={j}>{s}</li>)}
                              </ul>
                            </div>
                            <div>
                              <span className="text-red-400 text-xs uppercase">Concerns</span>
                              <ul className="text-sm text-gray-300 list-disc list-inside mt-1">
                                {reaction.topConcerns.map((c, j) => <li key={j}>{c}</li>)}
                              </ul>
                            </div>
                            <div>
                              <span className="text-cyber-blue text-xs uppercase">Suggestions</span>
                              <ul className="text-sm text-gray-300 list-disc list-inside mt-1">
                                {reaction.refinementSuggestions.map((s, j) => <li key={j}>{s}</li>)}
                              </ul>
                            </div>
                          </div>
                          <details className="mt-3">
                            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-300">
                              View Element-by-Element Reactions
                            </summary>
                            <div className="mt-3 space-y-2">
                              {reaction.elementReactions.map((er, j) => (
                                <div key={j} className="bg-cyber-gray p-3 rounded flex flex-wrap items-start gap-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-white">{er.element}</span>
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        er.elementType === 'ad' ? 'bg-cyber-blue/20 text-cyber-blue' :
                                        er.elementType === 'social' ? 'bg-cyber-pink/20 text-cyber-pink' :
                                        er.elementType === 'event' ? 'bg-green-500/20 text-green-400' :
                                        er.elementType === 'promo' ? 'bg-cyber-purple/20 text-cyber-purple' :
                                        'bg-yellow-500/20 text-yellow-400'
                                      }`}>{er.elementType}</span>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">{er.emotionalResponse}</p>
                                    <p className="text-xs text-gray-500 mt-1">{er.reasoning}</p>
                                    <div className="flex gap-3 mt-2 text-xs">
                                      <span className={er.wouldShare ? 'text-green-400' : 'text-gray-600'}>
                                        {er.wouldShare ? 'Would share' : 'Unlikely to share'}
                                      </span>
                                      <span className={er.wouldEngage ? 'text-green-400' : 'text-gray-600'}>
                                        {er.wouldEngage ? 'Would engage' : 'Unlikely to engage'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className={`text-xl font-display ${getScoreColor(er.resonanceScore)}`}>
                                    {er.resonanceScore}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </details>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Run Again */}
                <div className="text-center">
                  <button onClick={handleRunSimulation} className="cyber-button-secondary" disabled={isSimulating}>
                    {isSimulating ? 'Running...' : 'Run New Simulation'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Chat Section */}
        {activeSection === 'chat' && (
          <ChatInterface chatSession={chatSession} reportTitle={report.showInfo.title} />
        )}
      </div>

      {/* Hidden PDF Export Content - renders all sections for full multi-page PDF */}
      {isExportingPdf && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '800px' }}>
          <div id="pdf-export-content" style={{ background: '#0d0d1a', color: '#e0e0e0', padding: '30px', fontFamily: 'system-ui, sans-serif' }}>
            {/* Title Page */}
            <div style={{ textAlign: 'center', paddingBottom: '40px', borderBottom: '2px solid #05d9e8' }}>
              <div style={{ fontSize: '12px', color: '#05d9e8', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '10px' }}>
                {report.mediaType === 'tv' ? 'TV Show' : 'Movie'} | {report.campaignType === 'fyc' ? 'FYC Awards Campaign' : 'Consumer Campaign'}
              </div>
              <h1 style={{ fontSize: '32px', color: '#ffffff', margin: '10px 0' }}>{report.showInfo.title}</h1>
              <p style={{ fontSize: '14px', color: '#888' }}>
                Marketing Strategy Report | Generated {new Date(report.createdAt).toLocaleDateString()} | Regions: {report.selectedRegions.map(getRegionLabel).join(', ')}
              </p>
            </div>

            {/* Show Info */}
            <div style={{ marginTop: '30px' }} className="html2pdf__page-break">
              <h2 style={{ fontSize: '22px', color: '#05d9e8', borderBottom: '1px solid #05d9e8', paddingBottom: '8px' }}>Show Information</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '16px' }}>
                <div>
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Genre</span>
                    <p style={{ margin: '4px 0' }}>{report.showInfo.genre}{report.showInfo.subGenre && ` / ${report.showInfo.subGenre}`}</p>
                  </div>
                  {report.showInfo.network && (
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Network</span>
                      <p style={{ margin: '4px 0' }}>{report.showInfo.network}</p>
                    </div>
                  )}
                  {report.showInfo.studio && (
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Studio</span>
                      <p style={{ margin: '4px 0' }}>{report.showInfo.studio}</p>
                    </div>
                  )}
                  {report.showInfo.releaseDate && (
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Release Date</span>
                      <p style={{ margin: '4px 0' }}>{report.showInfo.releaseDate}</p>
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Cast</span>
                    <p style={{ margin: '4px 0' }}>{report.showInfo.cast.join(', ')}</p>
                  </div>
                  {report.showInfo.creators && (
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Creators</span>
                      <p style={{ margin: '4px 0' }}>{report.showInfo.creators.join(', ')}</p>
                    </div>
                  )}
                  {report.showInfo.director && (
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Director</span>
                      <p style={{ margin: '4px 0' }}>{report.showInfo.director}</p>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ marginTop: '12px' }}>
                <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Summary</span>
                <p style={{ margin: '4px 0', lineHeight: '1.6' }}>{report.showInfo.summary}</p>
              </div>
            </div>

            {/* Audience Profile */}
            <div style={{ marginTop: '30px' }} className="html2pdf__page-break">
              <h2 style={{ fontSize: '22px', color: '#05d9e8', borderBottom: '1px solid #05d9e8', paddingBottom: '8px' }}>Audience Profile</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginTop: '16px' }}>
                <div style={{ background: '#1a1a2e', padding: '16px', borderRadius: '8px' }}>
                  <span style={{ fontSize: '11px', color: '#ff2a6d', textTransform: 'uppercase' }}>Primary Age</span>
                  <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '8px 0 0' }}>
                    {report.audienceProfile.primaryAge.min}-{report.audienceProfile.primaryAge.max}
                  </p>
                  {report.audienceProfile.secondaryAge && (
                    <p style={{ fontSize: '12px', color: '#888' }}>Secondary: {report.audienceProfile.secondaryAge.min}-{report.audienceProfile.secondaryAge.max}</p>
                  )}
                </div>
                <div style={{ background: '#1a1a2e', padding: '16px', borderRadius: '8px' }}>
                  <span style={{ fontSize: '11px', color: '#ff2a6d', textTransform: 'uppercase' }}>Gender Split</span>
                  <p style={{ margin: '8px 0 4px', fontSize: '14px' }}>Male: {report.audienceProfile.gender.male}%</p>
                  <p style={{ margin: '0', fontSize: '14px' }}>Female: {report.audienceProfile.gender.female}%</p>
                </div>
                <div style={{ background: '#1a1a2e', padding: '16px', borderRadius: '8px' }}>
                  <span style={{ fontSize: '11px', color: '#ff2a6d', textTransform: 'uppercase' }}>Income</span>
                  <p style={{ margin: '8px 0 2px', fontSize: '14px' }}>Low: {report.audienceProfile.income.low}%</p>
                  <p style={{ margin: '0 0 2px', fontSize: '14px' }}>Middle: {report.audienceProfile.income.middle}%</p>
                  <p style={{ margin: '0', fontSize: '14px' }}>High: {report.audienceProfile.income.high}%</p>
                </div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Locations</span>
                <p style={{ margin: '4px 0' }}>{report.audienceProfile.locations.join(', ')}</p>
              </div>
              <div style={{ marginTop: '12px' }}>
                <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Interests</span>
                <p style={{ margin: '4px 0' }}>{report.audienceProfile.interests.join(', ')}</p>
              </div>
              <div style={{ marginTop: '12px' }}>
                <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Psychographics</span>
                <p style={{ margin: '4px 0' }}>{report.audienceProfile.psychographics.join(', ')}</p>
              </div>
              <div style={{ marginTop: '12px' }}>
                <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Media Consumption</span>
                <p style={{ margin: '4px 0' }}>
                  Streaming: {report.audienceProfile.mediaConsumption.streaming}% | Linear TV: {report.audienceProfile.mediaConsumption.linear}% | Social: {report.audienceProfile.mediaConsumption.social}%
                  {report.audienceProfile.mediaConsumption.theatrical !== undefined && ` | Theatrical: ${report.audienceProfile.mediaConsumption.theatrical}%`}
                </p>
              </div>
            </div>

            {/* Competitor Analysis */}
            <div style={{ marginTop: '30px' }} className="html2pdf__page-break">
              <h2 style={{ fontSize: '22px', color: '#05d9e8', borderBottom: '1px solid #05d9e8', paddingBottom: '8px' }}>Competitor Analysis</h2>
              {report.competitorAnalysis.competitors.map((comp, i) => (
                <div key={i} style={{ background: '#1a1a2e', padding: '16px', borderRadius: '8px', marginTop: '12px', borderLeft: '3px solid #05d9e8' }}>
                  <h3 style={{ fontSize: '18px', color: '#ffffff', margin: '0 0 8px' }}>{comp.title}</h3>
                  <p style={{ fontSize: '13px', color: '#888', margin: '0 0 8px' }}>{comp.similarity}</p>
                  <p style={{ fontSize: '13px', margin: '0 0 8px' }}><strong>Performance:</strong> {comp.performance.metric}: {comp.performance.value}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Success Factors</span>
                      <ul style={{ margin: '4px 0', paddingLeft: '18px', fontSize: '13px' }}>
                        {comp.successFactors.map((f, j) => <li key={j}>{f}</li>)}
                      </ul>
                    </div>
                    <div>
                      <span style={{ fontSize: '11px', color: '#9d4edd', textTransform: 'uppercase' }}>Lessons</span>
                      <ul style={{ margin: '4px 0', paddingLeft: '18px', fontSize: '13px' }}>
                        {comp.lessons.map((l, j) => <li key={j}>{l}</li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
              <div style={{ marginTop: '16px' }}>
                <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Market Gap</span>
                <p style={{ margin: '4px 0', fontSize: '13px' }}>{report.competitorAnalysis.marketGap}</p>
              </div>
              <div style={{ marginTop: '12px' }}>
                <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Positioning</span>
                <p style={{ margin: '4px 0', fontSize: '13px' }}>{report.competitorAnalysis.positioning}</p>
              </div>
              <div style={{ marginTop: '12px' }}>
                <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Differentiators</span>
                <p style={{ margin: '4px 0', fontSize: '13px' }}>{report.competitorAnalysis.differentiators.join(', ')}</p>
              </div>
            </div>

            {/* Regional Marketing Plans - ALL regions */}
            {report.selectedRegions.map((region) => {
              const plan = report.regionalPlans[region]
              if (!plan) return (
                <div key={region} style={{ marginTop: '30px' }} className="html2pdf__page-break">
                  <h2 style={{ fontSize: '22px', color: '#05d9e8', borderBottom: '1px solid #05d9e8', paddingBottom: '8px' }}>
                    Marketing Plan: {getRegionLabel(region)}
                  </h2>
                  <p style={{ color: '#888', fontStyle: 'italic' }}>No plan was generated for this region.</p>
                </div>
              )
              return (
                <div key={region} style={{ marginTop: '30px' }} className="html2pdf__page-break">
                  <h2 style={{ fontSize: '22px', color: '#05d9e8', borderBottom: '1px solid #05d9e8', paddingBottom: '8px' }}>
                    Marketing Plan: {getRegionLabel(region)}
                  </h2>

                  {/* Budget */}
                  {plan.budgetBreakdown && (
                    <div style={{ marginTop: '16px' }}>
                      <h3 style={{ fontSize: '16px', color: '#ff2a6d', margin: '0 0 12px' }}>Budget Overview</h3>
                      <p style={{ fontSize: '24px', fontWeight: 'bold', textAlign: 'center', margin: '8px 0 16px' }}>
                        Total: {formatCurrency(plan.totalBudget || 0)}
                      </p>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #333' }}>
                            <th style={{ textAlign: 'left', padding: '8px', color: '#05d9e8' }}>Category</th>
                            <th style={{ textAlign: 'right', padding: '8px', color: '#05d9e8' }}>Amount</th>
                            <th style={{ textAlign: 'right', padding: '8px', color: '#05d9e8' }}>%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plan.budgetBreakdown.map((item, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #222' }}>
                              <td style={{ padding: '8px' }}>{item.category}</td>
                              <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(item.amount)}</td>
                              <td style={{ padding: '8px', textAlign: 'right' }}>{item.percentage}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Ad Placements */}
                  {plan.adPlacements && plan.adPlacements.length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                      <h3 style={{ fontSize: '16px', color: '#ff2a6d', margin: '0 0 12px' }}>Ad Placements</h3>
                      {plan.adPlacements.map((ad, i) => (
                        <div key={i} style={{ background: '#1a1a2e', padding: '12px', borderRadius: '8px', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div>
                              <strong style={{ color: '#fff' }}>{ad.platform}</strong>
                              <span style={{ color: '#888', marginLeft: '8px', fontSize: '12px' }}>{ad.format}</span>
                            </div>
                            <span style={{ color: '#05d9e8' }}>{formatCurrency(ad.budget)}</span>
                          </div>
                          <p style={{ fontSize: '12px', color: '#aaa', margin: '6px 0 0' }}>{ad.reasoning}</p>
                          <p style={{ fontSize: '11px', color: '#666', margin: '4px 0 0' }}>Reach: {ad.expectedReach} | Timing: {ad.timing}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Social Strategy */}
                  {plan.socialStrategy && plan.socialStrategy.length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                      <h3 style={{ fontSize: '16px', color: '#ff2a6d', margin: '0 0 12px' }}>Social Strategy</h3>
                      {plan.socialStrategy.map((social, i) => (
                        <div key={i} style={{ background: '#1a1a2e', padding: '12px', borderRadius: '8px', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <strong style={{ color: '#fff' }}>{social.platform}</strong>
                            <span style={{ color: '#ff2a6d' }}>{formatCurrency(social.budget)}</span>
                          </div>
                          <p style={{ fontSize: '12px', color: '#888', margin: '4px 0' }}>Frequency: {social.frequency}</p>
                          <p style={{ fontSize: '12px', color: '#aaa', margin: '0' }}>Content: {social.content.join(', ')}</p>
                          {social.influencerStrategy && <p style={{ fontSize: '12px', color: '#aaa', margin: '4px 0 0' }}>Influencers: {social.influencerStrategy}</p>}
                          <p style={{ fontSize: '11px', color: '#9d4edd', margin: '4px 0 0' }}>#{social.hashtags.join(' #')}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Cross Promotions */}
                  {plan.crossPromotions && plan.crossPromotions.length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                      <h3 style={{ fontSize: '16px', color: '#ff2a6d', margin: '0 0 12px' }}>Cross Promotions</h3>
                      {plan.crossPromotions.map((promo, i) => (
                        <div key={i} style={{ background: '#1a1a2e', padding: '12px', borderRadius: '8px', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <strong style={{ color: '#fff' }}>{promo.partner}</strong>
                            <span style={{ color: '#05d9e8' }}>{formatCurrency(promo.budget)}</span>
                          </div>
                          <p style={{ fontSize: '12px', color: '#888', margin: '2px 0' }}>{promo.type}</p>
                          <p style={{ fontSize: '12px', color: '#aaa', margin: '0' }}>{promo.description}</p>
                          <p style={{ fontSize: '11px', color: '#9d4edd', margin: '4px 0 0' }}>Impact: {promo.expectedImpact}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Stunts & Events */}
                  {plan.stuntsEvents && plan.stuntsEvents.length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                      <h3 style={{ fontSize: '16px', color: '#ff2a6d', margin: '0 0 12px' }}>Stunts & Events</h3>
                      {plan.stuntsEvents.map((event, i) => (
                        <div key={i} style={{ background: '#1a1a2e', padding: '12px', borderRadius: '8px', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <strong style={{ color: '#fff' }}>{event.name}</strong>
                            <span style={{ color: '#ff2a6d' }}>{formatCurrency(event.budget)}</span>
                          </div>
                          <p style={{ fontSize: '12px', color: '#888', margin: '2px 0' }}>{event.type} | {event.location} | {event.timing}</p>
                          <p style={{ fontSize: '12px', color: '#aaa', margin: '0' }}>{event.description}</p>
                          <p style={{ fontSize: '11px', color: '#00ff88', margin: '4px 0 0' }}>Media Value: {event.mediaValue}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Timeline */}
                  {plan.timeline && plan.timeline.length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                      <h3 style={{ fontSize: '16px', color: '#ff2a6d', margin: '0 0 12px' }}>Campaign Timeline</h3>
                      {plan.timeline.map((phase, i) => (
                        <div key={i} style={{ borderLeft: '3px solid #05d9e8', paddingLeft: '16px', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <strong style={{ color: '#fff' }}>{phase.phase}</strong>
                            <span style={{ fontSize: '12px', color: '#888' }}>{phase.duration}</span>
                          </div>
                          <ul style={{ margin: '4px 0', paddingLeft: '18px', fontSize: '13px', color: '#aaa' }}>
                            {phase.activities.map((a, j) => <li key={j}>{a}</li>)}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}

            {/* FYC Strategy */}
            {report.campaignType === 'fyc' && report.fycStrategy && (
              <div style={{ marginTop: '30px' }} className="html2pdf__page-break">
                <h2 style={{ fontSize: '22px', color: '#ffd700', borderBottom: '1px solid #ffd700', paddingBottom: '8px' }}>FYC Awards Campaign</h2>

                <div style={{ marginTop: '16px' }}>
                  <h3 style={{ fontSize: '16px', color: '#ffd700', margin: '0 0 8px' }}>Campaign Narrative</h3>
                  <p style={{ fontSize: '13px', lineHeight: '1.6' }}>{report.fycStrategy.overallNarrative}</p>
                </div>

                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ fontSize: '16px', color: '#ffd700', margin: '0 0 12px' }}>Recommended Categories</h3>
                  {report.fycStrategy.primaryCategories.map((cat, i) => (
                    <div key={i} style={{ background: '#1a1a2e', padding: '12px', borderRadius: '8px', marginBottom: '8px', borderLeft: '3px solid #ffd700' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong style={{ color: '#ffd700', fontSize: '16px' }}>{cat.category}</strong>
                        <span style={{ fontSize: '11px', color: cat.likelihood === 'high' ? '#00ff88' : cat.likelihood === 'medium' ? '#ffd700' : '#ff6b35', textTransform: 'uppercase' }}>
                          {cat.likelihood} likelihood
                        </span>
                      </div>
                      <p style={{ fontSize: '12px', color: '#888', margin: '6px 0' }}>{cat.reasoning}</p>
                      <p style={{ fontSize: '12px', margin: '4px 0' }}><span style={{ color: '#05d9e8' }}>Angle:</span> {cat.campaignAngle}</p>
                      <p style={{ fontSize: '12px', margin: '4px 0' }}><span style={{ color: '#ff2a6d' }}>Voter Messaging:</span> {cat.voterMessaging}</p>
                      <p style={{ fontSize: '11px', color: '#888', margin: '4px 0' }}>Competition: {cat.competingTitles.join(', ')}</p>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ fontSize: '16px', color: '#ffd700', margin: '0 0 12px' }}>FYC Screenings</h3>
                  {report.fycStrategy.screenings.map((screening, i) => (
                    <div key={i} style={{ background: '#1a1a2e', padding: '12px', borderRadius: '8px', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                          <strong style={{ color: '#fff' }}>{screening.name}</strong>
                          <p style={{ fontSize: '12px', color: '#888', margin: '2px 0' }}>{screening.venue} | {screening.location}</p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ color: '#05d9e8' }}>{formatCurrency(screening.budget)}</span>
                          <p style={{ fontSize: '11px', color: '#666', margin: '2px 0' }}>{screening.date}</p>
                        </div>
                      </div>
                      <p style={{ fontSize: '12px', color: '#aaa', margin: '6px 0' }}>{screening.description}</p>
                      <p style={{ fontSize: '11px', color: '#888' }}>Attendees: {screening.targetAttendees.join(', ')}</p>
                      <p style={{ fontSize: '11px', color: '#888' }}>Talent: {screening.talentAttending.join(', ')}</p>
                      {screening.qAndA && <p style={{ fontSize: '11px', color: '#00ff88' }}>Includes Q&A Session</p>}
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ fontSize: '16px', color: '#ffd700', margin: '0 0 12px' }}>Branch Outreach Strategy</h3>
                  {report.fycStrategy.voterOutreach.map((outreach, i) => (
                    <div key={i} style={{ background: '#1a1a2e', padding: '12px', borderRadius: '8px', marginBottom: '8px' }}>
                      <strong style={{ color: '#05d9e8' }}>{outreach.branch}</strong>
                      <p style={{ fontSize: '12px', color: '#aaa', margin: '6px 0' }}>{outreach.strategy}</p>
                      <p style={{ fontSize: '12px', color: '#aaa', margin: '0' }}>Key Messages: {outreach.keyMessages.join('; ')}</p>
                      <p style={{ fontSize: '11px', color: '#888', margin: '4px 0 0' }}>Touchpoints: {outreach.touchpoints.join(', ')}</p>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ fontSize: '16px', color: '#ffd700', margin: '0 0 12px' }}>Trade Ad Placements</h3>
                  {report.fycStrategy.tradeAdPlacements.map((ad, i) => (
                    <div key={i} style={{ background: '#1a1a2e', padding: '12px', borderRadius: '8px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                      <div>
                        <strong style={{ color: '#fff' }}>{ad.publication}</strong>
                        <p style={{ fontSize: '12px', color: '#888', margin: '2px 0' }}>{ad.format} | {ad.timing}</p>
                        <p style={{ fontSize: '12px', color: '#aaa', margin: '0' }}>{ad.messaging}</p>
                      </div>
                      <span style={{ color: '#05d9e8' }}>{formatCurrency(ad.budget)}</span>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ fontSize: '16px', color: '#ffd700', margin: '0 0 12px' }}>Guild Screenings</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {report.fycStrategy.guildScreenings.map((guild, i) => (
                      <div key={i} style={{ background: '#1a1a2e', padding: '12px', borderRadius: '8px' }}>
                        <strong style={{ color: '#ffd700' }}>{guild.guild}</strong>
                        <p style={{ fontSize: '12px', color: '#888', margin: '2px 0' }}>{guild.date} | {guild.location}</p>
                        <p style={{ fontSize: '12px', color: '#aaa', margin: '4px 0 0' }}>{guild.strategy}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* FYC Budget */}
                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ fontSize: '16px', color: '#ffd700', margin: '0 0 12px' }}>FYC Campaign Budget</h3>
                  <p style={{ fontSize: '24px', fontWeight: 'bold', textAlign: 'center', margin: '8px 0 16px' }}>
                    Total: {formatCurrency(report.fycStrategy.totalBudget)}
                  </p>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #333' }}>
                        <th style={{ textAlign: 'left', padding: '8px', color: '#ffd700' }}>Category</th>
                        <th style={{ textAlign: 'right', padding: '8px', color: '#ffd700' }}>Amount</th>
                        <th style={{ textAlign: 'right', padding: '8px', color: '#ffd700' }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.fycStrategy.budgetBreakdown.map((item, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #222' }}>
                          <td style={{ padding: '8px' }}>{item.category}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(item.amount)}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{item.percentage}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* FYC Timeline */}
                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ fontSize: '16px', color: '#ffd700', margin: '0 0 12px' }}>Awards Season Timeline</h3>
                  {report.fycStrategy.timeline.map((phase, i) => (
                    <div key={i} style={{ borderLeft: '3px solid #ffd700', paddingLeft: '16px', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <strong style={{ color: '#fff' }}>{phase.phase}</strong>
                        <span style={{ fontSize: '12px', color: '#888' }}>{phase.duration}</span>
                      </div>
                      <ul style={{ margin: '4px 0', paddingLeft: '18px', fontSize: '13px', color: '#aaa' }}>
                        {phase.activities.map((a, j) => <li key={j}>{a}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Key Art Concepts */}
            <div style={{ marginTop: '30px' }} className="html2pdf__page-break">
              <h2 style={{ fontSize: '22px', color: '#05d9e8', borderBottom: '1px solid #05d9e8', paddingBottom: '8px' }}>Key Art Concepts</h2>
              {keyArtConcepts.map((concept) => (
                <div key={concept.id} style={{ background: '#1a1a2e', padding: '16px', borderRadius: '8px', marginTop: '12px', borderLeft: '3px solid #9d4edd' }}>
                  <h3 style={{ fontSize: '18px', color: '#ffffff', margin: '0 0 8px' }}>{concept.name}</h3>
                  <p style={{ fontSize: '13px', color: '#aaa', margin: '0 0 8px' }}>{concept.description}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '13px' }}>
                    <div>
                      <span style={{ fontSize: '11px', color: '#05d9e8', textTransform: 'uppercase' }}>Mood</span>
                      <p style={{ margin: '4px 0' }}>{concept.mood}</p>
                    </div>
                    <div>
                      <span style={{ fontSize: '11px', color: '#ff2a6d', textTransform: 'uppercase' }}>Tagline</span>
                      <p style={{ margin: '4px 0', fontStyle: 'italic' }}>"{concept.tagline}"</p>
                    </div>
                    <div>
                      <span style={{ fontSize: '11px', color: '#9d4edd', textTransform: 'uppercase' }}>Color Palette</span>
                      <p style={{ margin: '4px 0' }}>{concept.colorPalette.join(', ')}</p>
                    </div>
                  </div>
                  {concept.generatedImageUrl && (
                    <div style={{ marginTop: '12px' }}>
                      <img src={concept.generatedImageUrl} alt={concept.name} style={{ maxWidth: '400px', borderRadius: '8px' }} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Simulation Results (if available) */}
            {simulation && (
              <div style={{ marginTop: '30px' }} className="html2pdf__page-break">
                <h2 style={{ fontSize: '22px', color: '#9d4edd', borderBottom: '1px solid #9d4edd', paddingBottom: '8px' }}>Audience Simulation Results</h2>

                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <span style={{ fontSize: '48px', fontWeight: 'bold', color: (simulation.overallScore || 0) >= 8 ? '#00ff88' : (simulation.overallScore || 0) >= 6 ? '#ffd700' : '#ff6b35' }}>
                    {typeof simulation.overallScore === 'number' ? simulation.overallScore.toFixed(1) : parseFloat((simulation.overallScore||0) as unknown as string).toFixed(1)}
                  </span>
                  <span style={{ fontSize: '18px', color: '#666' }}>/10</span>
                  <p style={{ fontSize: '14px', color: '#aaa', margin: '8px 0' }}>{simulation.summary}</p>
                </div>

                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ fontSize: '16px', color: '#9d4edd', margin: '0 0 12px' }}>Top Recommendations</h3>
                  <ol style={{ paddingLeft: '20px', fontSize: '13px', color: '#aaa' }}>
                    {(simulation.topRecommendations || []).map((rec, i) => <li key={i} style={{ marginBottom: '6px' }}>{rec}</li>)}
                  </ol>
                </div>

                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ fontSize: '16px', color: '#9d4edd', margin: '0 0 12px' }}>Persona Reactions</h3>
                  {(simulation.personas || []).map((persona) => {
                    const reaction = (simulation.reactions || []).find(r => r.personaId === persona.id)
                    return (
                      <div key={persona.id} style={{ background: '#1a1a2e', padding: '12px', borderRadius: '8px', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <span style={{ fontSize: '20px', marginRight: '8px' }}>{persona.avatar}</span>
                            <strong style={{ color: '#fff' }}>{persona.name}</strong>
                            <span style={{ color: '#888', fontSize: '12px', marginLeft: '8px' }}>{persona.age}, {persona.occupation}, {persona.location}</span>
                          </div>
                          {reaction && (
                            <span style={{ fontSize: '24px', fontWeight: 'bold', color: (reaction.overallResonance || 0) >= 8 ? '#00ff88' : (reaction.overallResonance || 0) >= 6 ? '#ffd700' : '#ff6b35' }}>
                              {typeof reaction.overallResonance === 'number' ? reaction.overallResonance.toFixed(1) : parseFloat((reaction.overallResonance||0) as unknown as string).toFixed(1)}
                            </span>
                          )}
                        </div>
                        {reaction && (
                          <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '12px' }}>
                            <div>
                              <span style={{ color: '#00ff88', fontSize: '11px', textTransform: 'uppercase' }}>Strengths</span>
                              <ul style={{ margin: '4px 0', paddingLeft: '16px', color: '#aaa' }}>
                                {(reaction.topStrengths || []).map((s, j) => <li key={j}>{s}</li>)}
                              </ul>
                            </div>
                            <div>
                              <span style={{ color: '#ff6b35', fontSize: '11px', textTransform: 'uppercase' }}>Concerns</span>
                              <ul style={{ margin: '4px 0', paddingLeft: '16px', color: '#aaa' }}>
                                {(reaction.topConcerns || []).map((c, j) => <li key={j}>{c}</li>)}
                              </ul>
                            </div>
                            <div>
                              <span style={{ color: '#05d9e8', fontSize: '11px', textTransform: 'uppercase' }}>Suggestions</span>
                              <ul style={{ margin: '4px 0', paddingLeft: '16px', color: '#aaa' }}>
                                {(reaction.refinementSuggestions || []).map((s, j) => <li key={j}>{s}</li>)}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>

                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ fontSize: '16px', color: '#9d4edd', margin: '0 0 12px' }}>Campaign Element Rankings</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #333' }}>
                        <th style={{ textAlign: 'left', padding: '8px', color: '#9d4edd' }}>#</th>
                        <th style={{ textAlign: 'left', padding: '8px', color: '#9d4edd' }}>Element</th>
                        <th style={{ textAlign: 'left', padding: '8px', color: '#9d4edd' }}>Type</th>
                        <th style={{ textAlign: 'right', padding: '8px', color: '#9d4edd' }}>Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(simulation.rankedElements || []).map((element, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #222' }}>
                          <td style={{ padding: '8px' }}>{i + 1}</td>
                          <td style={{ padding: '8px' }}>{element.element}</td>
                          <td style={{ padding: '8px', textTransform: 'uppercase', fontSize: '11px' }}>{element.elementType}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: (element.averageScore || 0) >= 8 ? '#00ff88' : (element.averageScore || 0) >= 6 ? '#ffd700' : '#ff6b35' }}>
                            {typeof element.averageScore === 'number' ? element.averageScore.toFixed(1) : parseFloat((element.averageScore||0) as unknown as string).toFixed(1)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
